import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";

const MEDIA_DIR = "/media/tactsuit/tactglove-dk3/vrchat";

/**
 * 순서는 시안 그대로 headpat → experience → hug (#6025).
 *
 * `featured` 는 모바일에서 맨 위 전폭으로 올라가는 컷이다 — 세로 영상(540×640)이라
 * 2열 칸(185px)에 넣으면 인물이 잘린다. 시안 `≤700px` 규칙이 이것 하나만 따로 키운다.
 */
export const DK3_VRCHAT_CLIPS = [
  { id: "headpat", featured: false },
  { id: "experience", featured: true },
  { id: "hug", featured: false },
] as const;

type ClipId = (typeof DK3_VRCHAT_CLIPS)[number]["id"];

/**
 * `prefers-reduced-motion: reduce` 구독. `matchMedia` 가 없으면(jsdom · 오래된 웹뷰)
 * «감소 안 함» 으로 본다 — `floating-popup` 의 같은 질의와 같은 판단이다.
 */
const reducedMotionQuery = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;
const subscribeReducedMotion = (cb: () => void) => {
  const m = reducedMotionQuery();
  m?.addEventListener("change", cb);
  return () => m?.removeEventListener("change", cb);
};

/**
 * TactGlove DK3 PDP 의 VRChat 영상 섹션 (#6025).
 *
 * ## 재생 규칙 — 시안 스크립트의 `sync()` 를 옮겼다
 *
 * ```
 *   재생 = wantsPlay(토글) && inView(몽타주가 20% 이상 보임)
 *   wantsPlay 초기값 = 「모션 줄이기」가 꺼져 있으면 재생, 켜져 있으면 정지
 * ```
 *
 * 「모션 줄이기」 사용자도 토글로 재생할 수 있다 — 누른 뒤에는 그 선택(`userWantsPlay`)이
 * 이긴다. 초기값을 effect 의 setState 로 덮지 않는 것은 그러면 토글 표시와 실제 재생이 한
 * 프레임 어긋나기 때문이다(PR #6039 R1 2번).
 *
 * 토글 하나가 **세 영상을 함께** 멈추고, 화면 밖에서는 토글과 무관하게 멈춘다.
 * `autoPlay` 속성은 주지 않는다 — 그러면 화면 밖에서도 먼저 돌기 시작하고, 실제
 * 재생 여부는 어차피 이 관찰자가 정한다.
 *
 * ## 스타일 범위
 *
 * 색·크기는 전부 이 파일 안의 임의값이다 — 시안이 사이트 토큰에 없는 어두운 판
 * (`#0b111b`)이라 공용 토큰을 늘리지 않는다. 브레이크포인트는 시안의 700/1000 을
 * 사이트의 `tablet`(768)·`laptop`(1024)에 맞췄다.
 */
export default function Dk3VrchatStory() {
  const { t } = useTrans(["tactsuit"]);
  const montageRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    () => reducedMotionQuery()?.matches ?? false,
    () => false,
  );
  const [userWantsPlay, setUserWantsPlay] = useState<boolean | null>(null);
  const wantsPlay = userWantsPlay ?? !reducedMotion;

  /**
   * ⚠️ 키를 **리터럴로** 부른다 — 미사용 키 판정 도구가 `t("…")` 를 grep 한다
   * (`docs/i18n.md` 「번역 키는 코드에 리터럴로 남겨야 한다」 · PR #6039 R1 1번).
   * 키를 `DK3_VRCHAT_CLIPS` 의 필드로 들고 다니면 그 도구가 못 본다.
   */
  const captions: Record<ClipId, { small: string; strong: string }> = {
    headpat: {
      small: t("dk3VrchatCaption1Small"),
      strong: t("dk3VrchatCaption1"),
    },
    experience: {
      small: t("dk3VrchatCaption2Small"),
      strong: t("dk3VrchatCaption2"),
    },
    hug: {
      small: t("dk3VrchatCaption3Small"),
      strong: t("dk3VrchatCaption3"),
    },
  };
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const montage = montageRef.current;
    if (!montage || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 },
    );
    observer.observe(montage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const playing = wantsPlay && inView;
    videoRefs.current.forEach((video) => {
      if (!video) return;
      if (playing) video.play().catch(() => {});
      else video.pause();
    });
  }, [wantsPlay, inView]);

  return (
    <section
      aria-labelledby="dk3-vrchat-title"
      className="w-full px-4 tablet:px-5"
    >
      <div className="mx-auto max-w-[1440px]">
        <div className="overflow-hidden rounded-2xl bg-[#0b111b]">
          <header className="block px-[23px] pb-[23px] pt-[26px] tablet:flex tablet:items-center tablet:justify-between tablet:gap-6 tablet:p-[26px] laptop:px-[34px] laptop:pb-[28px] laptop:pt-[30px]">
            <div>
              <p className="mb-2.5 text-[9px] font-500 tracking-[1.65px] text-[#99baff] tablet:text-[10px]">
                TACTGLOVE DK3
              </p>
              <h2
                id="dk3-vrchat-title"
                className="max-w-[270px] text-[25px] font-500 leading-[1.5] tracking-[-0.8px] text-white tablet:max-w-none tablet:leading-[1.4] tablet:tracking-[-1.1px] laptop:text-[30px]"
              >
                {t("dk3VrchatTitle")}
              </h2>
              <p className="mt-3 max-w-[280px] text-[12px] leading-[1.7] tracking-[-0.15px] text-[#b0bac9] tablet:mt-[11px] tablet:max-w-none tablet:text-[13px]">
                {t("dk3VrchatDescription")}
              </p>
            </div>
            <div className="mt-5 tablet:mt-0 tablet:shrink-0">
              <button
                type="button"
                onClick={() => setUserWantsPlay(!wantsPlay)}
                aria-label={
                  wantsPlay ? t("dk3VrchatPause") : t("dk3VrchatPlay")
                }
                aria-pressed={!wantsPlay}
                className="flex h-[42px] w-[42px] items-center justify-center rounded-full border border-[#ffffff40] text-[13px] text-white transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[5px] focus-visible:outline-[#7faaff]"
              >
                <span aria-hidden="true">{wantsPlay ? "Ⅱ" : "▶"}</span>
              </button>
            </div>
          </header>

          <div
            ref={montageRef}
            className="grid grid-cols-2 gap-[3px] tablet:h-[330px] tablet:grid-cols-[1.08fr_0.84fr_1.08fr] tablet:gap-1 laptop:h-[400px]"
          >
            {DK3_VRCHAT_CLIPS.map((clip, i) => (
              <figure
                key={clip.id}
                className={cn(
                  "relative m-0 h-[185px] overflow-hidden bg-[#15202d] tablet:h-auto",
                  clip.featured &&
                    "order-first col-span-2 h-[330px] tablet:order-none tablet:col-span-1",
                )}
              >
                <video
                  ref={(el) => {
                    videoRefs.current[i] = el;
                  }}
                  src={`${MEDIA_DIR}/${clip.id}.mp4`}
                  poster={`${MEDIA_DIR}/${clip.id}-poster.jpg`}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-0 h-full w-full object-cover saturate-[.92]",
                    clip.featured && "object-[50%_28%] tablet:object-center",
                  )}
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-[linear-gradient(180deg,#07101d10_25%,#07101d00_48%,#07101d45_70%,#07101de8_100%)]"
                />
                <figcaption
                  className={cn(
                    "absolute text-white [text-shadow:0_1px_12px_#0004]",
                    clip.featured
                      ? "bottom-6 left-[23px] right-2.5"
                      : "bottom-[15px] left-[15px] right-2.5",
                    "tablet:bottom-[22px] tablet:left-5 tablet:right-5 laptop:bottom-[27px] laptop:left-7",
                  )}
                >
                  <small
                    className={cn(
                      "block tracking-[0.5px] text-[#d9e1ec]",
                      clip.featured
                        ? "mb-2 text-[10px]"
                        : "mb-[5px] text-[8px]",
                      "tablet:mb-2 tablet:text-[10px]",
                    )}
                  >
                    {captions[clip.id].small}
                  </small>
                  <strong
                    className={cn(
                      "block font-500 leading-[1.5]",
                      clip.featured
                        ? "text-[23px] tracking-[-0.65px]"
                        : "text-[14px] tracking-[-0.4px]",
                      "tablet:text-[19px] tablet:tracking-[-0.65px] laptop:text-[23px]",
                    )}
                  >
                    {captions[clip.id].strong}
                  </strong>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <p className="mt-3.5 block text-[9px] leading-[1.7] text-[#7f8997] tablet:text-right tablet:text-[10px]">
          {t("dk3VrchatNote")}
        </p>
      </div>
    </section>
  );
}
